import {BrowserApi, ColorArray, Cookie, CookieStore, Listener, Manifest, MessageSender, Port, Tab} from "@lib/browser/BrowserApi";

declare var browser: {
  windows: {
    get(id: number): Promise<Window>;
    update(id: number, updateOptions: {focused: boolean; drawAttention: boolean}): void;
  };
  browserAction: {
    disable(tabId?: number): Promise<void>;
    enable(tabId?: number): Promise<void>;
    setBadgeBackgroundColor(details: {
      color: ColorArray,
      tabId?: number
    }): Promise<void>;
    setBadgeText(details: {
      text: Nullable<string>,
      tabId?: number
    }): Promise<void>;
    setBadgeTextColor(details: {
      color: ColorArray,
      tabId?: number
    }): Promise<void>;
    setIcon(details: {
      imageData?: ImageData | { [key: number]: ImageData };
      path?: string | { [key: number]: string };
      tabId?: number;
    }): Promise<void>
    setTitle(details: {
      title: Nullable<string>,
      tabId?: number
    }): Promise<void>;
  };

  cookies: {
    /**
     * Requires 'cookies' permission.
     */
    getAll(details: {
      url?: string;
      storeId?: string;
    }): Promise<Cookie[]>;
    /**
     * Requires 'cookies' permission.
     */
    getAllCookieStores(): Promise<CookieStore[]>;
  };

  runtime: {
    readonly lastError: Nullable<any>;
    readonly id: string;

    onConnect: Listener<(port: Port) => void>;
    onMessage: Listener<(message: object, sender: MessageSender) => Promise<any>>;

    connect(extensionID?: string, connectInfo?: {
      name?: string
    }): Port;
    /**
     * Requires 'nativeMessaging' permission.
     */
    connectNative(application: string): Port;
    getManifest(): Manifest;
    sendMessage(message: any): Promise<any>;
  };

  tabs: {
    executeScript(tabID: Optional<number>, details: {
      allFrames?: boolean;
      file?: string;
      frameId?: number;
      runAt?: "document_start" | "document_end" | "document_idle";
    }): Promise<any[]>;
    getTab(tabID: number): Promise<Tab>;
    reload(id: number): void;
    query(queryOptions: {url: string}): Promise<Tab[]>;
  };

  storage: {
    managed: {
      get<T>(key: string): Promise<T | null>;
    }
  };

  alarms: {
    create(name: string, alarmInfo: {delayInMinutes?: number, periodInMinutes?: number, when?: number}, callback?: Function): Promise<any>;
    onAlarm: Listener<(alarm: { name: string, periodInMinutes?: number, scheduledTime: number}) => void>;
  }
}

export const FirefoxBrowser: BrowserApi = {
  browserAction: {
    disable(tabID)
    {
      return browser.browserAction.disable(tabID);
    },
    enable(tabID)
    {
      return browser.browserAction.enable(tabID);
    },
    setBadgeBackgroundColor(details)
    {
      return browser.browserAction.setBadgeBackgroundColor(details);
    },
    setIcon(details)
    {
      return browser.browserAction.setIcon(details);
    },
    setBadgeText(details)
    {
      return browser.browserAction.setBadgeText(details);
    },
    setBadgeTextColor(details)
    {
      return browser.browserAction.setBadgeTextColor(details);
    },
    setTitle(details)
    {
      if (details.title === null)
        details.title = "";

      return browser.browserAction.setTitle(details);
    }
  },
  cookies: {
    getAll(details)
    {
      return browser.cookies.getAll(details);
    },
    getAllCookieStores()
    {
      return browser.cookies.getAllCookieStores();
    }
  },
  runtime: {
    get id(): string
    {
      return browser.runtime.id;
    },
    get lastError(): Nullable<string>
    {
      return browser.runtime.lastError;
    },
    onConnect: {
      addListener(callback)
      {
        browser.runtime.onConnect.addListener(callback);
      },
      removeListener(callback)
      {
        browser.runtime.onConnect.removeListener(callback);
      }
    },
    onMessage: {
      addListener(callback)
      {
        browser.runtime.onMessage.addListener(callback);
      },
      removeListener(callback)
      {
        browser.runtime.onMessage.removeListener(callback);
      }
    },
    connect(extensionID, connectInfo): Port
    {
      return browser.runtime.connect(extensionID, connectInfo);
    },
    connectNative(application): Port
    {
      return browser.runtime.connectNative(application);
    },
    getManifest(): Manifest
    {
      return browser.runtime.getManifest();
    },
    sendMessage(message: any): Promise<any>
    {
      return browser.runtime.sendMessage(message);
    }
  },
  tabs: {
    executeScript(tabID, details): Promise<any[]>
    {
      return browser.tabs.executeScript(tabID, details);
    },
    getTab(tabID): Promise<Tab>
    {
      return browser.tabs.getTab(tabID);
    },
    reload(id: number) {
      return browser.tabs.reload(id);
    },
    query(queryOptions: { url: string }): Promise<Tab[]> {
      return browser.tabs.query(queryOptions);
    }
  },
  storage: {
    local: {
      get(key: string): Promise<any> {
        return new Promise(() => localStorage.getItem(key));
      },
      remove(keys: string): Promise<void> {
        return new Promise(() => {
          localStorage.removeItem(keys);
        })
      },
      set(items: { [p: string]: any }): Promise<void> {
        return new Promise(() => {
          for (let key in items) {
            localStorage.setItem(key, items[key]);
          }
        });
      }
    },
    managed: {
      get(key: string): Promise<any> {
        return new Promise(() => browser.storage.managed.get(key));
      },
    }
  },
  windows: {
    get(id: number): Promise<Window> {
      return browser.windows.get(id);
    },
    update(id: number, updateOptions: { focused: boolean; drawAttention: boolean }) {
      return browser.windows.update(id, updateOptions);
    }
  },
  alarms: {
    create(name: string, alarmInfo: {delayInMinutes?: number, periodInMinutes?: number, when?: number}, callback?: Function): Promise<any> {
      return browser.alarms.create(name, alarmInfo);
    },
    onAlarm: {
      addListener(callback)
      {
        browser.alarms.onAlarm.addListener(callback);
      },
      removeListener(callback)
      {
        browser.alarms.onAlarm.removeListener(callback);
      }
    }
  }
}